import { HI_TYPES, type HiType } from '../constants';
import { DOCUMENT_TYPES } from './codes';
import type { Bundle, Composition, FhirResource, Reference } from './types';

export interface FhirIssue {
  path: string;
  message: string;
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/** The ABDM health information type a document's Composition declares, or null. */
export function hiTypeOf(bundle: Bundle): HiType | null {
  const composition = bundle.entry?.[0]?.resource;
  if (composition?.resourceType !== 'Composition') return null;
  for (const hiType of HI_TYPES) {
    const expected = DOCUMENT_TYPES[hiType].type;
    const coding = expected.coding?.[0];
    if (coding) {
      if (composition.type.coding?.some((c) => c.system === coding.system && c.code === coding.code)) return hiType;
    } else if (composition.type.text?.toLowerCase() === expected.text?.toLowerCase()) {
      return hiType;
    }
  }
  return null;
}

function references(value: unknown, path: string, found: { path: string; reference: string }[]): void {
  if (Array.isArray(value)) {
    value.forEach((item, i) => references(item, `${path}[${i}]`, found));
  } else if (isObject(value)) {
    for (const [key, child] of Object.entries(value)) {
      if (key === 'reference' && typeof child === 'string') found.push({ path, reference: child });
      else references(child, path ? `${path}.${key}` : key, found);
    }
  }
}

/**
 * Structural checks for a FHIR document bundle, received or built: document
 * type, identifier, timestamp and version, unique full URLs, a Composition
 * first with a known type, a Patient subject, authors, and every in-bundle
 * reference resolving. Returns no issues when all pass. Profile validation
 * against the ABDM guide comes in phase 12.
 */
export function checkDocument(value: unknown, expected?: HiType): FhirIssue[] {
  const issues: FhirIssue[] = [];
  const add = (path: string, message: string) => issues.push({ path, message });
  if (!isObject(value) || value.resourceType !== 'Bundle') return [{ path: '', message: 'Not a FHIR Bundle' }];
  const bundle = value as unknown as Bundle;
  if (bundle.type !== 'document') add('type', 'Must be document');
  if (!bundle.identifier?.value) add('identifier', 'Required');
  if (!bundle.timestamp) add('timestamp', 'Required');
  if (!bundle.meta?.versionId) add('meta.versionId', 'Required');
  const entries = Array.isArray(bundle.entry) ? bundle.entry : [];
  if (entries.length === 0) {
    add('entry', 'A document needs entries');
    return issues;
  }

  const byUrl = new Map<string, FhirResource>();
  entries.forEach((entry, i) => {
    if (!entry.fullUrl) add(`entry[${i}].fullUrl`, 'Required');
    else if (entry.fullUrl.includes('/_history/')) add(`entry[${i}].fullUrl`, 'Cannot be version specific');
    else if (byUrl.has(entry.fullUrl)) add(`entry[${i}].fullUrl`, 'Repeated');
    if (!isObject(entry.resource) || typeof entry.resource.resourceType !== 'string') add(`entry[${i}].resource`, 'Required');
    else if (entry.fullUrl) byUrl.set(entry.fullUrl, entry.resource);
  });

  const composition = entries[0]?.resource;
  if (composition?.resourceType !== 'Composition') {
    add('entry[0]', 'The first entry must be a Composition');
    return issues;
  }
  const resolve = (ref: Reference | undefined) => (ref?.reference ? byUrl.get(ref.reference) : undefined);
  const doc = composition as Composition;
  const hiType = hiTypeOf(bundle);
  if (!hiType) add('entry[0].resource.type', 'Unknown document type');
  else if (expected && hiType !== expected) add('entry[0].resource.type', `Expected a ${expected} document`);
  if (!doc.title) add('entry[0].resource.title', 'Required');
  if (!doc.date) add('entry[0].resource.date', 'Required');
  if (resolve(doc.subject)?.resourceType !== 'Patient') add('entry[0].resource.subject', 'Must point to a Patient in the bundle');
  if (!Array.isArray(doc.author) || doc.author.length === 0) add('entry[0].resource.author', 'Required');
  else doc.author.forEach((author, i) => {
    if (!resolve(author)) add(`entry[0].resource.author[${i}]`, 'Must point to a resource in the bundle');
  });

  const found: { path: string; reference: string }[] = [];
  entries.forEach((entry, i) => references(entry.resource, `entry[${i}].resource`, found));
  for (const { path, reference } of found) {
    if (reference.startsWith('urn:uuid:') && !byUrl.has(reference)) add(path, `Points to ${reference}, which is not in the bundle`);
  }
  return issues;
}

const DISPENSE_STATUSES = new Set(['completed', 'declined']);

/**
 * Structural checks for a dispensing record: a collection bundle with an
 * identifier and timestamp, unique full URLs, and at least one
 * MedicationDispense that is completed or declined, names the patient and
 * the medicine in the bundle, and says which prescription item it answers.
 * Every in-bundle reference must resolve. Returns no issues when all pass.
 */
export function checkDispenseRecord(value: unknown): FhirIssue[] {
  const issues: FhirIssue[] = [];
  const add = (path: string, message: string) => issues.push({ path, message });
  if (!isObject(value) || value.resourceType !== 'Bundle') return [{ path: '', message: 'Not a FHIR Bundle' }];
  const bundle = value as unknown as Bundle;
  if (bundle.type !== 'collection') add('type', 'Must be collection');
  if (!bundle.identifier?.value) add('identifier', 'Required');
  if (!bundle.timestamp || Number.isNaN(Date.parse(bundle.timestamp))) add('timestamp', 'Required');
  const entries = Array.isArray(bundle.entry) ? bundle.entry : [];
  const byUrl = new Map<string, FhirResource>();
  entries.forEach((entry, i) => {
    if (!entry.fullUrl) add(`entry[${i}].fullUrl`, 'Required');
    else if (byUrl.has(entry.fullUrl)) add(`entry[${i}].fullUrl`, 'Repeated');
    if (!isObject(entry.resource) || typeof entry.resource.resourceType !== 'string') add(`entry[${i}].resource`, 'Required');
    else if (entry.fullUrl) byUrl.set(entry.fullUrl, entry.resource);
  });
  const resolve = (ref: Reference | undefined) => (ref?.reference ? byUrl.get(ref.reference) : undefined);
  let dispenses = 0;
  entries.forEach((entry, i) => {
    const resource = entry.resource;
    if (resource?.resourceType !== 'MedicationDispense') return;
    dispenses += 1;
    const path = `entry[${i}].resource`;
    if (!DISPENSE_STATUSES.has(resource.status)) add(`${path}.status`, 'Must be completed or declined');
    if (resolve(resource.subject)?.resourceType !== 'Patient') add(`${path}.subject`, 'Must point to a Patient in the bundle');
    const named = resource.medicationCodeableConcept?.text ?? (resolve(resource.medicationReference) as { code?: { text?: string } } | undefined)?.code?.text;
    if (!named) add(`${path}.medication`, 'Must name the medicine');
    if (resource.medicationReference && resolve(resource.medicationReference)?.resourceType !== 'Medication') {
      add(`${path}.medicationReference`, 'Must point to a Medication in the bundle');
    }
    if (!resource.authorizingPrescription?.[0]?.identifier?.value) add(`${path}.authorizingPrescription`, 'Required');
    if (resource.quantity && (typeof resource.quantity.value !== 'number' || !(resource.quantity.value > 0) || !resource.quantity.unit)) {
      add(`${path}.quantity`, 'Needs a positive value and a unit');
    }
  });
  if (dispenses === 0) add('entry', 'A dispensing record needs at least one MedicationDispense');
  const found: { path: string; reference: string }[] = [];
  entries.forEach((entry, i) => references(entry.resource, `entry[${i}].resource`, found));
  for (const { path, reference } of found) {
    if (reference.startsWith('urn:uuid:') && !byUrl.has(reference)) add(path, `Points to ${reference}, which is not in the bundle`);
  }
  return issues;
}
